/**
 * GpuParticleEmitter — per-emitter facade over a GpuParticlePool + a billboard
 * InstancedMesh, mirroring the CPU `ParticleEmitter` config surface so callers
 * can swap backends behind `IParticleEmitter`.
 *
 * Emission is scheduled CPU-side (rate accumulator + burst scheduler, identical
 * semantics to the CPU emitter); the heavy per-particle simulation runs in the
 * pool's compute kernel, dispatched by `GpuParticleSystem` each frame.
 *
 * The mesh is an InstancedMesh of a unit quad with `count = capacity`; the
 * material reads per-instance state from the pool's storage buffers, so we
 * never touch instanceMatrix on the CPU. Dead particles collapse to degenerate
 * (size-0) quads in the vertex stage.
 */

import * as THREE from 'three/webgpu';
import type { EngineRenderer } from '../../render/Renderer.js';
import { DEFAULT_PARTICLE_CONFIG } from '../types.js';
import type { ParticleConfig } from '../types.js';
import { GpuParticlePool } from './GpuParticlePool.js';
import { createGpuParticleMaterial, type GpuParticleMaterial } from './GpuParticleMaterial.js';
import { RateAccumulator, BurstScheduler } from './emission.js';
import { rampFromConfig } from './ramp.js';
import type { GpuParticleConfig, IParticleEmitter, ParticleBackend } from './types.js';

const _origin = new THREE.Vector3();
/** Scratch for the emitter basis handed to the spawn kernel (see _extractTransform). */
const _basis = new THREE.Matrix4();

export class GpuParticleEmitter implements IParticleEmitter {
  readonly config: GpuParticleConfig;
  readonly maxParticles: number;
  readonly backend: ParticleBackend = 'gpu';
  readonly worldMatrix = new THREE.Matrix4();

  /** The renderable mesh — add to the scene (or an entity's Object3D). */
  readonly mesh: THREE.InstancedMesh;

  private pool: GpuParticlePool;
  private material: GpuParticleMaterial;
  private geometry: THREE.PlaneGeometry;
  private rate: RateAccumulator;
  private bursts: BurstScheduler;
  private _playing = true;
  private _elapsed = 0;
  private _disposed = false;
  /** Continuous emission rate set via setEmissionRate(); null = use config. */
  private _rateOverride: number | null = null;

  constructor(renderer: EngineRenderer, config?: Partial<GpuParticleConfig>) {
    this.config = { ...DEFAULT_PARTICLE_CONFIG, ...config };
    this.maxParticles = this.config.maxParticles;

    this.pool = new GpuParticlePool(renderer, { capacity: this.maxParticles });
    this.pool.configureSpawn(this.config);
    this.pool.configureForces(this.config);
    this.pool.clear();

    // Billboard quad. Unit plane; the material scales corners by per-particle
    // size, so geometry size is irrelevant beyond providing the 4 corners + uv.
    this.geometry = new THREE.PlaneGeometry(1, 1);

    this.material = createGpuParticleMaterial({
      positionAttribute: this.pool.positionAttribute(),
      velocityAttribute: this.pool.velocityAttribute(),
      miscAttribute: this.pool.miscAttribute(),
      ramp: rampFromConfig(this.config),
      map: this.config.texture ?? null,
      blending: this.config.blending ?? THREE.AdditiveBlending,
      depthWrite: this.config.depthWrite ?? false,
      // Streak sprites follow their velocity on screen (see renderMode).
      velocityStretch: this.config.renderMode === 'stretched' ? (this.config.velocityStretch ?? 2.5) : 0,
      sixWay: this.config.sixWay ?? null,
      sizeGate: this.config.sizeGate ?? null, // render wave 1: the roof map's exposure
    });

    this.mesh = new THREE.InstancedMesh(this.geometry, this.material, this.pool.capacity);
    this.mesh.frustumCulled = false; // simulation is world-space; cull manually
    this.mesh.count = this.pool.capacity;
    this.mesh.name = 'GpuParticleEmitter';

    this.rate = new RateAccumulator();
    this.bursts = new BurstScheduler(this.config.emission?.bursts);
  }

  get playing(): boolean { return this._playing; }

  play(): void { this._playing = true; }
  pause(): void { this._playing = false; }
  stop(): void {
    this._playing = false;
    this._elapsed = 0;
    this.rate.reset();
    this.bursts.reset();
    this.pool.clear();
  }

  /**
   * Emit `count` particles immediately at the emitter's current world origin.
   */
  emit(count: number): void {
    if (this._disposed || count <= 0) return;
    this._extractTransform();
    this.pool.spawn(count, _origin, _basis);
  }

  /**
   * Advance the emitter. Schedules spawn batches from the rate accumulator +
   * bursts, and ticks the pool's simulation. `dt` is real seconds.
   */
  update(dt: number): void {
    if (this._disposed || !this._playing) {
      // Even when paused we keep the simulation frozen (no update dispatch).
      return;
    }
    this._elapsed += dt;
    this._extractTransform();

    // Continuous emission. setEmissionRate() override wins over config so the
    // emission rate can change after create without mutating the read-only config.
    const rate = this._rateOverride ?? this.config.emission?.rateOverTime ?? 0;
    const continuous = this.rate.tick(rate, dt);
    if (continuous > 0) this.pool.spawn(continuous, _origin, _basis);

    // Bursts.
    const burst = this.bursts.tick(dt);
    if (burst > 0) this.pool.spawn(burst, _origin, _basis);

    // Simulation step (ages + integrates on the GPU).
    //
    // PARITY NOTE: spawn happens BEFORE this aging update, so particles spawned
    // this frame are aged by a full `dt` on their spawn frame. The CPU
    // `ParticleEmitter` ages FIRST, then emits, so its new particles get age 0
    // on their spawn frame — i.e. the GPU path effectively ages new particles
    // one frame "earlier" than the CPU path. The visual difference is one
    // frame's worth of fade/integration on the very first frame; lifetimes and
    // steady-state behavior match. See docs/subsystems/gpu-particles.md.
    this.pool.update(dt, this._elapsed);
  }

  getActiveCount(): number {
    return this.pool.activeEstimate;
  }

  /**
   * Set the continuous emission rate (particles/sec). Stored as an override
   * rather than mutating the read-only `config` (IParticleEmitter contract).
   */
  setEmissionRate(rate: number): void {
    this._rateOverride = rate;
  }

  /** Pool slot capacity (rounded up to pow2 from maxParticles). */
  get capacity(): number {
    return this.pool.capacity;
  }

  /** Re-push force uniforms after mutating `config.forces`. */
  syncForces(): void {
    this.pool.configureForces(this.config);
  }

  /** Re-push spawn-shape uniforms after mutating shape/speed/lifetime/size. */
  syncSpawn(): void {
    this.pool.configureSpawn(this.config);
  }

  /** Replace the over-life color/size ramp. */
  setRamp(config: ParticleConfig = this.config): void {
    this.material.setRamp(rampFromConfig(config));
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
    this.mesh.dispose();
    this.pool.dispose();
  }

  /**
   * Resolve the transform the spawn kernel applies to emitter-local samples.
   *
   * The BASIS matters as much as the origin: shape samplers emit in local space,
   * so a rotated emitter (rain slanted into the wind, a waterfall crest leaning
   * downstream, upwind dust) was spawning axis-aligned on the GPU backend while
   * the WebGL2 CPU path — which applies the full world matrix — got it right.
   *
   * In `simulationSpace: 'local'` the mesh itself carries the emitter transform,
   * so both stay identity or the rotation would be applied twice.
   */
  private _extractTransform(): void {
    if (this.config.simulationSpace === 'local') {
      _origin.set(0, 0, 0);
      _basis.identity();
    } else {
      _origin.setFromMatrixPosition(this.worldMatrix);
      _basis.copy(this.worldMatrix);
    }
  }
}
